import { useCallback, useRef } from "react";

import { FormProvider } from "react-hook-form";

import Box from "@/components/Box";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { INVITE_YOUR_PATIENT_MUTATION } from "@/graphql/organisations/mutations";
import { CONNECTED_USERS_QUERY } from "@/graphql/organisations/queries/patients";
import useDjangoGraphqlForm from "@/hooks/form/useDjangoGraphqlForm";
import Checkbox from "@/tpo/Checkbox";
import ControlledFormField from "@/tpo/ControlledFormField";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import { IndicativeButton } from "@/v2/Buttons";

export default function InviteYourPatientForm({
  submitButtonLabel = "send request",
  onSuccess = () => {}
}) {
  const { organisation } = useOrganisationContext();

  const submitButtonRef = useRef();

  const { methods, onSubmit, nonFieldError } = useDjangoGraphqlForm({
    mutation: INVITE_YOUR_PATIENT_MUTATION,
    mutationName: "inviteYourPatientMutation",
    mutationOptions: {
      refetchQueries: [
        {
          query: CONNECTED_USERS_QUERY,
          variables: {
            page: 1,
            orderBy: "-created",
            search: "",
            organisationId: organisation?.id
          }
        }
      ]
    },
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      inviteToPlatform: false
    },
    handleSuccess: ({ api: { reset } }) => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(true);
        submitButtonRef.current.setPending(false);
        reset();
      }
      onSuccess();
    },
    handleFailure: () => {
      if (submitButtonRef.current) {
        submitButtonRef.current.setSuccessful(false);
        submitButtonRef.current.setPending(false);
      }
    }
  });

  const handleSubmitPatient = useCallback(
    input => {
      if (!organisation) return;
      onSubmit({
        organisation: parseInt(organisation.id),
        ...input
      });
    },
    [organisation, onSubmit]
  );

  return (
    <FormProvider {...methods}>
      <Box
        as="form"
        onSubmit={methods.handleSubmit(handleSubmitPatient)}
        data-component-name="InviteYourPatientForm"
      >
        {nonFieldError && <Error data-testid="nonFieldError" error={nonFieldError} />}
        <Stack gap={20}>
          <Group flexDirection={["column", "column", "row"]} gap={20} flexWrap="wrap">
            <ControlledFormField
              name="firstName"
              label="First Name"
              Component={FloatingLabelInput}
            />
            <ControlledFormField name="lastName" label="Last Name" Component={FloatingLabelInput} />
            <ControlledFormField name="email" label="Email" Component={FloatingLabelInput} />
          </Group>
          <ControlledFormField
            name="inviteToPlatform"
            label="Invite this patient to the patient platform"
            Component={Checkbox}
            styles={{
              labelProps: {
                fontSize: [12, 12, 14],
                lineHeight: "24px"
              },
              checkbox: {
                size: ["md", "md", "lg"]
              }
            }}
          />
          <IndicativeButton
            ref={submitButtonRef}
            defaultColor="green"
            pendingColor="#0cf970"
            successColor="#2ecc71"
            failureColor="error"
            size={["sm", "sm", "md"]}
            sx={{
              mr: "auto"
            }}
          >
            {submitButtonLabel}
          </IndicativeButton>
        </Stack>
      </Box>
    </FormProvider>
  );
}
